import { CanDeactivateFn, Routes } from '@angular/router';

import { environment } from 'src/environments/environment';
import { ACCESS_ROLES, APP_CONSTANTS } from './app.constants';
import { authGuard } from './core/guards/auth.guard';
import { roleGuard } from './core/guards/role.guard';
import { AddWellComponent } from './features/add-well/add-well.component';
import { DetailViewComponent } from './features/detail-view/detail-view.component';
import { UserAccessComponent } from './features/user-access/user-access.component';

// Prevents leaving detail view when the component has pending changes.
const canDeactivateDetailView: CanDeactivateFn<DetailViewComponent> = (component) =>
  component.canDeactivate();

// Prevents leaving add/duplicate well when the form has unsaved changes.
const canDeactivateAddWell: CanDeactivateFn<AddWellComponent> = (component) =>
  component.canDeactivate();

// Prevents leaving user access when the component has pending changes.
const canDeactivateUserAccess: CanDeactivateFn<UserAccessComponent> = (component) =>
  component.canDeactivate();

// Shared lazy loader for layout shell.
const loadLayoutComponent = () =>
  import('./layout/layout.component').then((m) => m.LayoutComponent);

const hmiRoutes: Routes = [
  {
    path: '',
    loadComponent: loadLayoutComponent,
    children: [
      {
        path: 'well/:id',
        loadComponent: () =>
          import('./features/detail-view/detail-view.component').then((m) => m.DetailViewComponent),
        canDeactivate: [canDeactivateDetailView],
      },
      {
        path: 'well/:id/developer',
        loadComponent: () =>
          import('./features/detail-view/detail-view.component').then((m) => m.DetailViewComponent),
        canDeactivate: [canDeactivateDetailView],
      },
      { path: '**', redirectTo: APP_CONSTANTS.DEFAULT_URL },
    ],
  },
];

// Standard application routes.
const defaultRoutes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./features/login/login.component').then((m) => m.LoginComponent),
  },
  {
    path: '',
    loadComponent: loadLayoutComponent,
    canActivate: [authGuard],
    children: [
      {
        path: 'map',
        loadComponent: () =>
          import('./features/map-view/map-view.component').then((m) => m.MapViewComponent),
      },
      {
        path: 'wells',
        loadComponent: () =>
          import('./features/list-view/advanced-search.component').then(
            (m) => m.AdvancedSearchComponent,
          ),
      },
      {
        path: 'groups',
        loadComponent: () =>
          import('./features/group-view/group-view.component').then((m) => m.GroupViewComponent),
      },
      {
        path: 'well',
        children: [
          {
            path: 'add',
            loadComponent: () =>
              import('./features/add-well/add-well.component').then((m) => m.AddWellComponent),
            canActivate: [roleGuard(ACCESS_ROLES.USER_ADMIN)],
            canDeactivate: [canDeactivateAddWell],
          },
          {
            path: 'duplicate/:id',
            loadComponent: () =>
              import('./features/add-well/add-well.component').then((m) => m.AddWellComponent),
            canActivate: [roleGuard(ACCESS_ROLES.USER_ADMIN)],
            canDeactivate: [canDeactivateAddWell],
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./features/detail-view/detail-view.component').then(
                (m) => m.DetailViewComponent,
              ),
            canDeactivate: [canDeactivateDetailView],
          },
          {
            path: ':id/developer',
            loadComponent: () =>
              import('./features/detail-view/detail-view.component').then(
                (m) => m.DetailViewComponent,
              ),
            canDeactivate: [canDeactivateDetailView],
          },
        ],
      },
      {
        path: 'user-access',
        loadComponent: () =>
          import('./features/user-access/user-access.component').then((m) => m.UserAccessComponent),
        canActivate: [roleGuard(ACCESS_ROLES.USER_ADMIN)],
        canDeactivate: [canDeactivateUserAccess],
      },
      {
        path: '',
        redirectTo: APP_CONSTANTS.DEFAULT_URL,
        pathMatch: 'full',
      },
    ],
  },
  {
    path: '**',
    redirectTo: '',
  },
];

// Select route set based on current runtime mode.
export const routes: Routes = environment.isHMIMode ? hmiRoutes : defaultRoutes;
